import {AnyAction} from 'redux';
import {ServerHolder} from './components/ContractEventlog';
import {ThunkDispatch} from 'redux-thunk';
import {EventlogState} from './EventlogState';

export enum EventlogActionType {
    SET_CURRENT_USER,
    FETCHING_EVENTS,
    SET_EVENTS,
    SEARCH_FILTER,
    FROM_DATE_FILTER,
    TO_DATE_FILTER,
    SINCE_LAST_LOGIN_FILTER,
    FETCHING_FEATURES,
    SET_FEATURES,
    FETCHING_ASSOCIATIONS,
    SET_ASSOCIATIONS,
    FETCHING_CONTRACT,
    SET_MILESTONES,
    FETCHING_FEATURE_TYPE_SUMMARY,
    SET_FEATURE_TYPE_SUMMARY,
    SET_COMMENTS,
    SET_FEATURE_TYPE_ISSUES,
    SET_FEATURE_TYPE_COMMENTS,
    FETCHING_FKB_FEATURES,
    SET_FKB_FEATURES,
    FETCHING_NVDB_SUBMISSIONS,
    SET_NVDB_SUBMISSIONS
}

export type EventlogThunk = (dispatch: ThunkDispatch<EventlogState, ServerHolder, AnyAction>, getState: () => EventlogState, serverHolder: ServerHolder) => void;

export type InitEventlog = (contractId: string) => EventlogThunk;
export const initEventlog: InitEventlog = (contractId) => (dispatch, getState, serverHolder) => {
    serverHolder.security.ensureCurrentUser()
        .then(user => {
            dispatch({
                type: EventlogActionType.SET_CURRENT_USER,
                currentUser: user
            });
            dispatch(fetchEvents(contractId));
            dispatch(setSearchFilter(''));
            dispatch(setFromDateFilter(null));
            dispatch(setToDateFilter(null));
            dispatch(setSinceLastLoginFilter(false));
        });
};

export type FetchEvents = (contractId: string) => EventlogThunk;
export const fetchEvents: FetchEvents = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_EVENTS,
        fetching: true
    });

    serverHolder.$q.all([serverHolder.server.getContractEvents(contractId)])
        .then(results => {
            const events = results[0];

            if (events) {

                dispatch({
                    type: EventlogActionType.SET_EVENTS,
                    events
                });
            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_EVENTS,
            fetching: false
        });
    });
};

export type FetchFeatures = (contractId: string) => EventlogThunk;
export const fetchFeatures: FetchFeatures = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_FEATURES,
        fetchingFeatures: true
    });

    serverHolder.$q.all([serverHolder.server.readFeatures(contractId, 'NVDB')])
        .then((data) => {
            const features = data[0].features;
            const featureTypeIssues = data[0].featureTypeIssues;
            const featureTypeComments = data[0].featureTypeComments;

            if (features && featureTypeIssues && featureTypeComments) {

                dispatch({
                    type: EventlogActionType.SET_FEATURES,
                    features
                });

                dispatch({
                    type: EventlogActionType.SET_FEATURE_TYPE_ISSUES,
                    featureTypeIssues
                });

                dispatch({
                    type: EventlogActionType.SET_FEATURE_TYPE_COMMENTS,
                    featureTypeComments
                });
            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_FEATURES,
            fetchingFeatures: false
        });
    });
};

export type FetchFkbFeatures = (contractId: string) => EventlogThunk;
export const fetchFkbFeatures: FetchFkbFeatures = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_FKB_FEATURES,
        fetchingFkbFeatures: true
    });

    serverHolder.$q.all([serverHolder.server.readFeatures(contractId, 'FKB')])
        .then((data) => {
            const fkbFeatures = data[0].features;

            if (fkbFeatures) {

                dispatch({
                    type: EventlogActionType.SET_FKB_FEATURES,
                    fkbFeatures
                });
            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_FKB_FEATURES,
            fetchingFkbFeatures: false
        });
    });
};

export type FetchAssociations = (contractId: string) => EventlogThunk;
export const fetchAssociations: FetchAssociations = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_ASSOCIATIONS,
        fetchingAssociations: true
    });

    serverHolder.$q.all([serverHolder.server.readAssociationsList(contractId)])
        .then(([associations]) => {
            if (associations) {
                dispatch({
                    type: EventlogActionType.SET_ASSOCIATIONS,
                    associations
                });
            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_ASSOCIATIONS,
            fetchingAssociations: false
        });
    });
};

export type FetchContract = (contractId: string) => EventlogThunk;
export const fetchContract: FetchContract = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_CONTRACT,
        fetchingContract: true
    });

    serverHolder.$q.all([serverHolder.server.readContract(contractId)])
        .then((data) => {
            const milestones = data[0].milestones;
            const comments = data[0].comments;

            if (milestones && comments) {

                dispatch({
                    type: EventlogActionType.SET_MILESTONES,
                    milestones
                });

                dispatch({
                    type: EventlogActionType.SET_COMMENTS,
                    comments
                });
            }
        }).finally(() => {
            dispatch({
                type: EventlogActionType.FETCHING_CONTRACT,
                fetchingContract: false
            });
    });
};

export type FetchFeatureTypeSummary = (contractId: string) => EventlogThunk;
export const fetchFeatureTypeSummary: FetchFeatureTypeSummary = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_FEATURE_TYPE_SUMMARY,
        fetchingFeatureTypeSummary: true
    });

    serverHolder.$q.all([serverHolder.server.getFeatureTypeSummary(contractId)])
        .then((data) => {
            const featureTypeSummary = data[0];

            if (featureTypeSummary) {

                dispatch({
                    type: EventlogActionType.SET_FEATURE_TYPE_SUMMARY,
                    featureTypeSummary
                });

            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_FEATURE_TYPE_SUMMARY,
            fetchingFeatureTypeSummary: false
        });
    });
};

export type FetchNvdbSubmissions = (contractId: string) => EventlogThunk;
export const fetchNvdbSubmissions: FetchNvdbSubmissions = (contractId) => async (dispatch, getState, serverHolder) => {

    dispatch({
        type: EventlogActionType.FETCHING_NVDB_SUBMISSIONS,
        fetchingNvdbSubmissions: true
    });

    serverHolder.$q.all([serverHolder.server.listSubmittedNvdbRegistrations(contractId)])
        .then((data) => {
            const nvdbSubmissions = data[0];

            if (nvdbSubmissions) {

                dispatch({
                    type: EventlogActionType.SET_NVDB_SUBMISSIONS,
                    nvdbSubmissions
                });
            }
        }).finally(() => {
        dispatch({
            type: EventlogActionType.FETCHING_NVDB_SUBMISSIONS,
            fetchingNvdbSubmissions: false
        });
    });
};

export type SetSearchFilter = (searchString: string) => EventlogThunk;
export const setSearchFilter: SetSearchFilter = (searchString) => (dispatch) => {
    dispatch({
        type: EventlogActionType.SEARCH_FILTER,
        searchString
    });
};

export type SetFromDateFilter = (fromDate: Date) => EventlogThunk;
export const setFromDateFilter: SetToDateFilter = (fromDate) => (dispatch) => {
    dispatch({
        type: EventlogActionType.FROM_DATE_FILTER,
        fromDate
    });
};

export type SetToDateFilter = (toDate: Date) => EventlogThunk;
export const setToDateFilter: SetToDateFilter = (toDate) => (dispatch) => {
    dispatch({
        type: EventlogActionType.TO_DATE_FILTER,
        toDate
    });
};

export type SetSinceLastLoginFilter = (lastLogin: boolean) => EventlogThunk;
export const setSinceLastLoginFilter: SetSinceLastLoginFilter = (lastLogin) => (dispatch) => {
    dispatch({
        type: EventlogActionType.SINCE_LAST_LOGIN_FILTER,
        lastLogin
    });
};